import { cn, UzButton } from '@utazzo/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import React, { Fragment, useState } from 'react';

import { useMarketingLeadType } from '../lead-type';
import { useMarketingFieldVisibility } from '../use-marketing-reports';

import { CostCell } from './CostCell';
import { signedTone, useGroupOpen } from './month-table';

import type {
  MarketingProfitMonth,
  MarketingProfitResponse,
  MarketingSpendMonth,
  MarketingSpendResponse,
} from '@utazzo/shared';

import { ReportSection } from '@/features/reports/components/ReportSection';

/* ------------------------------------------------------------------------ */
/*                                Formatting                                 */
/* ------------------------------------------------------------------------ */

const inr = (amount: number): string => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return amount.toLocaleString('en-IN');
  }
};

/** What the sheet shows where a formula has no denominator (`#DIV/0!`). */
const DASH = '-';

const count = (value: number): string => value.toLocaleString('en-IN');
const money = (value: number | null): string => (value === null ? DASH : inr(value));
const pct = (value: number | null): string => (value === null ? DASH : `${value.toFixed(2)}%`);
/** Raw count with its percentage in brackets, e.g. "123 (45.21%)". */
const countPct = (countValue: number, pctValue: number | null): string =>
  pctValue === null ? count(countValue) : `${count(countValue)} (${pctValue.toFixed(2)}%)`;
const days = (value: number | null): string => (value === null ? DASH : value.toFixed(0));
const multiple = (value: number | null): string => (value === null ? DASH : value.toFixed(2));

/** `YYYY-MM-DD` -> "6 Aug". */
const dayLabel = (iso: string): string => {
  const [, month, day] = iso.split('-').map(Number);
  const names = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${day} ${names[(month ?? 1) - 1] ?? ''}`;
};

/* ------------------------------------------------------------------------ */
/*                            The sheet's colours                            */
/* ------------------------------------------------------------------------ */

/**
 * The sheet is read as plain figures on a plain grid - no block fills.
 *
 * Colour is spent only where it carries meaning: profit reads green, a
 * loss-making Pure Profit red, and a figure the system worked out rather than
 * one that was typed reads grey. Everything else takes the normal text colour,
 * because a wall of tinted cells makes the two that matter impossible to find.
 */
type Tone = 'profit' | 'signed' | 'plain' | 'calc';

const TONE: Record<Exclude<Tone, 'signed'>, string> = {
  profit: 'text-emerald-700 dark:text-emerald-400',
  plain: 'text-foreground',
  // Ad spend, summed from the adsets - grey, so it is visibly not a cell to
  // type into.
  calc: 'text-muted-foreground',
};

const toneClass = (tone: Tone, value: number | null): string => {
  if (tone !== 'signed') return TONE[tone];
  if (value === null) return TONE.plain;
  return value < 0
    ? 'font-semibold text-rose-600 dark:text-rose-400'
    : 'font-semibold text-emerald-700 dark:text-emerald-400';
};

/**
 * Gridlines, Excel-style: every cell draws its own bottom and RIGHT edge, the
 * first column adds a left edge and the header row a top one. Drawn at full
 * `border-border` rather than a fainter tint - the sheet is read as a grid, and
 * a hairline that fades out turns it back into a list of numbers.
 */
const CELL = 'border-border border-b border-r px-3 py-1 text-right whitespace-nowrap tabular-nums';
const LABEL =
  'bg-card text-foreground border-border sticky left-0 z-10 border-r border-b border-l py-1 pr-3 pl-3 text-left align-middle text-[14px] font-normal whitespace-nowrap';
const BAND =
  'bg-muted text-foreground sticky left-0 z-10 border-border border-r border-b border-l px-3 py-1 text-left text-[12px] font-semibold tracking-wide uppercase';

/* ------------------------------------------------------------------------ */
/*                              Row definitions                              */
/* ------------------------------------------------------------------------ */

interface MetricRow {
  label: string;
  /** Measured against spend - left out of the Organic section, which has none. */
  spendBased?: boolean;
  tone: Tone;
  bold?: boolean;
  /** Sub-text under the label, e.g. how the figure is derived. */
  hint?: string;
  value: (month: MarketingProfitMonth) => string;
  /** The number behind the cell, for the signed tint. */
  signal?: (month: MarketingProfitMonth) => number | null;
}

interface RowGroup {
  label: string;
  /** Every row is measured against spend - the Organic section drops it. */
  spendBased?: boolean;
  rows: MetricRow[];
}

/**
 * The sheet's rows after the Spend band, in its order. Spend rows are built at
 * render time from the cost heads (one row per included head), so they live in
 * the component rather than here.
 */
const GROUPS: RowGroup[] = [
  {
    label: 'Profit',
    rows: [
      {
        label: 'Profit (Sales Tracker)',
        tone: 'profit',
        bold: true,
        hint: 'Gross markup on bookings closed',
        value: (m) => money(m.salesProfit),
      },
      {
        label: 'Pure Profit',
        spendBased: true,
        tone: 'signed',
        bold: true,
        hint: 'Profit minus total spend',
        value: (m) => money(m.pureProfit),
        signal: (m) => m.pureProfit,
      },
    ],
  },
  {
    label: 'Queries',
    rows: [
      { label: 'Query', tone: 'plain', bold: true, value: (m) => count(m.overall.query) },
      { label: 'Converted', tone: 'plain', value: (m) => count(m.overall.converted) },
      { label: '% Conversion', tone: 'plain', value: (m) => pct(m.overall.conversionPct) },
      { label: 'International Query', tone: 'plain', value: (m) => count(m.international.query) },
      {
        label: 'International Query Converted',
        tone: 'plain',
        value: (m) => count(m.international.converted),
      },
      {
        label: 'International Query % Conversion',
        tone: 'plain',
        value: (m) => pct(m.international.conversionPct),
      },
      { label: 'Domestic Query', tone: 'plain', value: (m) => count(m.domestic.query) },
      {
        label: 'Domestic Query Converted',
        tone: 'plain',
        value: (m) => count(m.domestic.converted),
      },
      {
        label: 'Domestic Query % Conversion',
        tone: 'plain',
        value: (m) => pct(m.domestic.conversionPct),
      },
      {
        label: 'Avg Order Value Domestic',
        tone: 'plain',
        value: (m) => money(m.domestic.avgOrderValue),
      },
      {
        label: 'Avg Order Value International',
        tone: 'plain',
        value: (m) => money(m.international.avgOrderValue),
      },
      {
        label: 'Avg Order Value overall',
        tone: 'plain',
        value: (m) => money(m.overall.avgOrderValue),
      },
      {
        label: 'Avg days of query to conversion',
        tone: 'plain',
        value: (m) => days(m.avgDaysToConversion),
      },
      {
        label: 'Query to Contactability Ratio',
        tone: 'plain',
        hint: 'Calls actually picked up',
        value: (m) => countPct(m.contactedCount, m.contactabilityRatio),
      },
      {
        label: 'Query to Active Ratio',
        tone: 'plain',
        hint: 'Ever reached active status',
        value: (m) => countPct(m.activeCount, m.activeRatio),
      },
      {
        label: 'Query to Warm Ratio',
        tone: 'plain',
        hint: 'Ever reached the warm sub-status',
        value: (m) => countPct(m.warmCount, m.warmRatio),
      },
      {
        label: 'Contactability to Active Ratio',
        tone: 'plain',
        hint: 'Of those contacted, reached active',
        value: (m) =>
          countPct(
            m.activeCount,
            m.contactedCount > 0 ? (m.activeCount / m.contactedCount) * 100 : null,
          ),
      },
      {
        label: '0 to 30',
        tone: 'plain',
        hint: 'Expected booking window',
        value: (m) =>
          countPct(m.paymentTimeline.within30Days.count, m.paymentTimeline.within30Days.pct),
      },
      {
        label: '30 to 60',
        tone: 'plain',
        value: (m) =>
          countPct(
            m.paymentTimeline.thirtyToSixtyDays.count,
            m.paymentTimeline.thirtyToSixtyDays.pct,
          ),
      },
      {
        label: 'greater than 60',
        tone: 'plain',
        value: (m) =>
          countPct(m.paymentTimeline.overSixtyDays.count, m.paymentTimeline.overSixtyDays.pct),
      },
    ],
  },
  {
    label: 'Efficiency',
    spendBased: true,
    rows: [
      {
        label: 'CPQ',
        tone: 'plain',
        hint: 'Total spend ÷ query',
        value: (m) => money(m.costPerQuery),
      },
      {
        label: 'CPQ at Meta Level',
        tone: 'plain',
        hint: 'Ad spend only ÷ query',
        value: (m) => money(m.cpqAtMetaLevel),
      },
      {
        label: 'ROI',
        tone: 'plain',
        hint: 'Pure profit ÷ total spend',
        value: (m) => multiple(m.roi),
      },
      {
        label: 'RoAS',
        tone: 'plain',
        hint: 'Profit ÷ total spend',
        value: (m) => multiple(m.roas),
      },
      { label: 'CAC', tone: 'plain', hint: 'Total spend ÷ converted', value: (m) => money(m.cac) },
    ],
  },
];

/**
 * Profit-sheet row label -> hideable catalog field key. A row whose key the
 * admin hid from this user is dropped (the API has also nulled the value). Rows
 * not listed here have no hideable key and always show.
 */
const ROW_FIELD_KEY: Record<string, string> = {
  'Profit (Sales Tracker)': 'salesProfit',
  'Pure Profit': 'pureProfit',
  '% Conversion': 'conversionPct',
  'International Query % Conversion': 'conversionPct',
  'Domestic Query % Conversion': 'conversionPct',
  'Avg Order Value Domestic': 'avgOrderValue',
  'Avg Order Value International': 'avgOrderValue',
  'Avg Order Value overall': 'avgOrderValue',
  'Query to Contactability Ratio': 'contactabilityRatio',
  'Query to Active Ratio': 'activeRatio',
  'Query to Warm Ratio': 'warmRatio',
  CPQ: 'costPerQuery',
  'CPQ at Meta Level': 'cpqAtMetaLevel',
  ROI: 'roi',
  RoAS: 'roas',
  CAC: 'cac',
};

/* ------------------------------------------------------------------------ */
/*                                 The sheet                                  */
/* ------------------------------------------------------------------------ */

interface Block {
  key: string;
  name: string;
  /** Oldest month first - left to right, as the sheet reads. */
  months: MarketingProfitMonth[];
  isOverall: boolean;
}

const BandRow = ({ span, children }: { span: number; children: React.ReactNode }) => (
  <tr>
    <th scope="rowgroup" className={BAND}>
      {children}
    </th>
    <td colSpan={span} className="bg-muted border-border border-r border-b" />
  </tr>
);

const Label = ({
  children,
  hint,
  bold,
}: {
  children: React.ReactNode;
  hint?: string;
  bold?: boolean;
}) => (
  <th scope="row" className={cn(LABEL, bold && 'font-semibold')}>
    <span className="block">{children}</span>
    {hint && (
      <span
        data-export-skip
        className="text-muted-foreground/70 block text-[11px] leading-tight font-normal"
      >
        {hint}
      </span>
    )}
  </th>
);

/**
 * "Query Profit and Spend" as ONE sheet, laid out like the client's Excel:
 * months across the top (oldest to newest), metrics down the side, the Overall
 * block first and then one block per lead source with the same rows - Meta,
 * Google, and so on - each under a yellow band that folds.
 *
 * The Spend rows are the cost heads the operator has ticked, one row each, and
 * they are typed INTO THE SHEET: click a cell, type, Enter. Ad spend is the one
 * cost that is never typed here (it is entered per adset on the Campaigns tab)
 * so its cells are grey. Everything below Spend is arithmetic.
 *
 * A month whose required costs are missing is not locked away; its profit and
 * efficiency cells simply read "-" until the figure is typed, and the amber
 * cost cell says which figure that is.
 */
export const ExcelProfitSheet = ({
  profit,
  spend,
  loading,
  canEdit,
}: {
  profit: MarketingProfitResponse | undefined;
  spend: MarketingSpendResponse | undefined;
  loading: boolean;
  canEdit: boolean;
}): React.JSX.Element => {
  /**
   * Which CHANNEL blocks are open. Empty to start, so the channels open folded
   * under an Overall block that is always open and never folds.
   *
   * Tracking the open ones rather than the closed ones is what makes "folded by
   * default" hold: the block list arrives with the data, so a set of closed keys
   * would start empty too and every block would read as open until something
   * filled it in.
   *
   * Folded is the right first view now that a folded band carries the block's
   * query count and pure profit - it opens on the channel comparison, and the
   * detail is one click away rather than forty rows of scrolling.
   */
  const groupOpen = useGroupOpen();
  // Organic leads cost nothing to bring in, so that section has no Spend band,
  // no Pure Profit and no efficiency ratios - only what the leads themselves did.
  const costless = useMarketingLeadType() === 'organic';
  const { isHidden } = useMarketingFieldVisibility();
  // Drop rows the admin hid from this user (per-user field visibility), then the
  // organic spend-based rows. Groups left empty afterwards are removed.
  const visibleRows = (rows: MetricRow[]): MetricRow[] =>
    rows.filter((row) => {
      const key = ROW_FIELD_KEY[row.label];
      if (key && isHidden(key)) return false;
      return !(costless && row.spendBased);
    });
  const groups = (costless ? GROUPS.filter((group) => !group.spendBased) : GROUPS)
    .map((group) => ({ ...group, rows: visibleRows(group.rows) }))
    .filter((group) => group.rows.length > 0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // The API returns newest first; the sheet reads oldest to newest.
  const months = [...(profit?.months ?? [])].reverse();
  const spendByMonth = new Map<string, MarketingSpendMonth>(
    (spend?.months ?? []).map((month) => [month.month, month]),
  );
  const blocks: Block[] = profit
    ? [
        { key: 'overall', name: 'Overall', months, isOverall: true },
        ...profit.channels.map((channel) => ({
          key: channel.key,
          name: channel.name,
          months: [...channel.months].reverse(),
          isOverall: false,
        })),
      ]
    : [];
  const span = months.length;
  const incomplete = costless ? [] : (spend?.months ?? []).filter((month) => !month.isComplete);

  // One switch for the whole sheet: a dozen channels is a dozen clicks to see
  // the report end to end, or to fold it back down to the channel comparison.
  // Only the channel blocks fold - Overall is always open - so with no channels
  // there is nothing for the switch to do and it is not offered.
  const foldable = blocks.filter((block) => !block.isOverall);
  const allFolded = foldable.every((block) => !expanded.has(block.key));
  const toggleAll = (): void =>
    setExpanded(allFolded ? new Set(foldable.map((block) => block.key)) : new Set());
  const actions =
    foldable.length > 0 ? (
      <UzButton variant="outline" size="sm" onClick={toggleAll}>
        {allFolded ? 'Expand all' : 'Collapse all'}
      </UzButton>
    ) : undefined;

  return (
    <ReportSection
      title={costless ? 'Query and Profit' : 'Query Profit and Spend'}
      actions={actions}
    >
      {loading || !profit ? (
        <div className="space-y-2 py-2">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <div key={index} className="bg-muted h-8 animate-pulse rounded" />
          ))}
        </div>
      ) : months.length === 0 ? (
        <p className="text-muted-foreground py-6 text-center text-sm">
          No months in the selected range.
        </p>
      ) : (
        <>
          {incomplete.length > 0 && (
            // The theme's warning token with foreground text, as the Alert
            // primitive does it. No `dark:` classes: that variant follows the
            // OS here, not the app theme, and painted this strip dark brown on
            // the light app for anyone whose machine runs dark.
            <p className="border-warning/40 bg-warning/10 text-foreground mb-3 rounded-md border px-3 py-2 text-sm font-medium">
              {incomplete.map((month) => month.label).join(', ')}:{' '}
              {incomplete.some((month) => month.allAccountSpend === null)
                ? 'no ad spend entered yet (Campaigns tab)'
                : 'a required cost is missing'}
              . Spend-based rows show "-" for those months until the figure is in.
            </p>
          )}
          <table className="w-full border-separate border-spacing-0 text-[15px]">
            <thead>
              <tr>
                <th className="bg-muted text-foreground border-border sticky top-0 left-0 z-40 border-t border-r border-b border-l px-3 py-2 text-left text-xs font-semibold tracking-wide uppercase">
                  Metric
                </th>
                {months.map((month) => (
                  <th
                    key={month.month}
                    className="bg-muted text-foreground border-border sticky top-0 z-20 border-t border-r border-b px-3 py-2 text-right text-xs font-semibold whitespace-nowrap"
                  >
                    {month.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {blocks.map((block) => {
                // Overall never folds: its Spend rows are where the required
                // costs get typed, and a folded block hides the very cells the
                // warning strip above is asking the operator to fill in.
                const folded = !block.isOverall && !groupOpen(expanded.has(block.key));
                // Cost rows: one per included head. The set is the same for every
                // month of a block, so the first month's list is the row order.
                const costCodes = block.months[0]?.costs.map((cost) => cost.code) ?? [];
                return (
                  <Fragment key={block.key}>
                    {/* Yellow channel band, as the sheet has it. Folds. */}
                    <tr>
                      <th
                        scope="rowgroup"
                        className="bg-muted text-foreground border-border sticky left-0 z-10 border-r border-b border-l px-2 py-1.5 text-left text-sm font-semibold"
                      >
                        {block.isOverall ? (
                          <span className="block truncate px-1">{block.name}</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => toggle(block.key)}
                            aria-expanded={!folded}
                            className="flex w-full items-center gap-1.5"
                          >
                            {folded ? (
                              <ChevronRight className="h-4 w-4 shrink-0" />
                            ) : (
                              <ChevronDown className="h-4 w-4 shrink-0" />
                            )}
                            <span className="truncate">{block.name}</span>
                          </button>
                        )}
                      </th>
                      {folded && block.months.length === span ? (
                        // Folded, the band is the only line this block gets, so
                        // it carries the block's headline figures rather than
                        // leaving a row of empty cells that says nothing.
                        block.months.map((month) => (
                          <td
                            key={month.month}
                            className="bg-muted border-border border-r border-b px-3 py-1 text-right whitespace-nowrap tabular-nums"
                          >
                            <span className="text-foreground block text-[13px] font-semibold">
                              {count(month.overall.query)}
                              <span className="text-muted-foreground ml-1 text-[10px] font-normal">
                                query
                              </span>
                            </span>
                            {costless ? (
                              <span className="block text-[11px] leading-tight text-emerald-700">
                                {money(month.salesProfit)}
                                <span className="text-muted-foreground ml-1 text-[10px]">
                                  profit
                                </span>
                              </span>
                            ) : (
                              <span
                                className={cn(
                                  'block text-[11px] leading-tight',
                                  month.pureProfit === null
                                    ? 'text-muted-foreground'
                                    : signedTone(month.pureProfit),
                                )}
                              >
                                {money(month.pureProfit)}
                                <span className="text-muted-foreground ml-1 text-[10px]">pure</span>
                              </span>
                            )}
                          </td>
                        ))
                      ) : (
                        <td colSpan={span} className="bg-muted border-border border-r border-b" />
                      )}
                    </tr>

                    {!folded && (
                      <>
                        {!costless && (
                          <>
                            <BandRow span={span}>Spend</BandRow>
                            {costCodes.map((code) => {
                              const first = block.months[0]!.costs.find((c) => c.code === code)!;
                              return (
                                <tr key={`${block.key}-cost-${code}`}>
                                  <Label
                                    hint={
                                      first.mode === 'DERIVED'
                                        ? 'Calculated · click to override'
                                        : first.mode === 'CARRY_FORWARD'
                                          ? 'Carries forward until changed'
                                          : undefined
                                    }
                                  >
                                    {first.name}
                                  </Label>
                                  {block.months.map((month) => {
                                    const cost = month.costs.find((c) => c.code === code);
                                    return cost ? (
                                      <CostCell
                                        key={month.month}
                                        cost={cost}
                                        month={month.month}
                                        canEdit={canEdit}
                                      />
                                    ) : (
                                      <td key={month.month} className={cn(CELL, TONE.plain)}>
                                        {DASH}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                            {!isHidden('allAccountSpend') && (
                              <tr>
                                <Label
                                  hint={
                                    block.isOverall
                                      ? 'Sum of adsets (Campaigns tab)'
                                      : 'Adsets whose leads came in here'
                                  }
                                >
                                  Account Spend
                                </Label>
                                {block.months.map((month) => {
                                  const meta = block.isOverall
                                    ? spendByMonth.get(month.month)
                                    : undefined;
                                  return (
                                    <td key={month.month} className={cn(CELL, TONE.calc)}>
                                      <span className="block">{money(month.allAccountSpend)}</span>
                                      {meta && meta.adsetCount > 0 && (
                                        <span
                                          data-export-skip
                                          className="block text-[10px] leading-tight"
                                        >
                                          {meta.adsetCount} adset{meta.adsetCount === 1 ? '' : 's'}
                                          {meta.spendAsOf
                                            ? ` · as of ${dayLabel(meta.spendAsOf)}`
                                            : ''}
                                        </span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            )}
                            {!isHidden('totalSpend') && (
                              <tr>
                                <Label bold hint="Account spend + every cost above">
                                  Total Spend
                                </Label>
                                {block.months.map((month) => (
                                  <td
                                    key={month.month}
                                    className={cn(CELL, TONE.plain, 'font-semibold')}
                                  >
                                    {money(month.totalSpend)}
                                  </td>
                                ))}
                              </tr>
                            )}
                          </>
                        )}

                        {groups.map((group) => (
                          <Fragment key={`${block.key}-${group.label}`}>
                            <BandRow span={span}>{group.label}</BandRow>
                            {group.rows.map((row) => (
                              <tr key={`${block.key}-${group.label}-${row.label}`}>
                                <Label bold={row.bold} hint={row.hint}>
                                  {row.label}
                                </Label>
                                {block.months.map((month) => (
                                  <td
                                    key={month.month}
                                    className={cn(
                                      CELL,
                                      toneClass(row.tone, row.signal ? row.signal(month) : null),
                                      row.bold && 'font-semibold',
                                    )}
                                  >
                                    {row.value(month)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </Fragment>
                        ))}
                      </>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </ReportSection>
  );
};
